
import { Vue, Component, Prop } from "vue-property-decorator";
import { Project_Activity, Project, User } from "@/generated/graphql";
import moment from "moment";
import {
  displayTimeInLocationTimezone,
  deserializeTimezone,
  getTimezoneFromLocationString,
} from "@/utils/timezoneUtils";
import { VUE_GOOGLEMAP_API_KEY } from "@/data/constants";
import visitTypeDisplayMap from "@/entities/VisitType";
import UIButton from "@/components/button/ui-button.vue";
import NotesModal from "@/components/projects/activities/NotesModal.vue";
import CalendarIcon from "@/components/icons/ClanderGray.vue";
import ClockIcon from "@/components/icons/ClockGray.vue";
import LocationIcon from "@/components/icons/ClockGray.vue";
import StarIcon from "@/components/icons/StartGray.vue";
import NotesView from "@/components/icons/NotesView.vue";

@Component({
  components: {
    UIButton,
    NotesModal,
    CalendarIcon,
    ClockIcon,
    LocationIcon,
    StarIcon,
    NotesView,
  },
})
export default class CompletedVisitCard extends Vue {
  @Prop({ type: Object, required: true }) readonly activity!: Project_Activity;
  @Prop({ type: Object, required: true }) readonly project!: Project;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;

  showNotesModal = false;
  locationTimezone: any = null;

  get clientName(): string {
    return this.project?.client_name || "Unknown Client";
  }

  get clientAge(): string {
    if (!this.project?.dob) return "n/a";

    const dob = new Date(this.project.dob);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    return age.toString();
  }

  get clientGender(): string {
    return this.project?.gender || "n/a";
  }

  get typeOfVisit(): string {
    if (!this.activity?.type_of_visit) return "n/a";
    return (
      visitTypeDisplayMap[this.activity.type_of_visit] ||
      this.activity.type_of_visit
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase())
    );
  }

  get formattedDate(): string {
    if (!this.activity?.start_date) return "n/a";
    return moment(this.activity.start_date).format("DD MMM, YYYY");
  }

  get timeRange(): string {
    const start = this.formatTime(this.activity?.start_time);
    const end = this.formatTime(this.activity?.end_time);

    if (!start || !end) return "n/a";

    const duration = this.calculateDuration();
    return duration ? `${start} - ${end} ${duration}` : `${start} - ${end}`;
  }

  calculateDuration(): string {
    if (!this.activity?.start_time || !this.activity?.end_time) return "";

    try {
      const startTime = moment(this.activity.start_time);
      const endTime = moment(this.activity.end_time);

      if (!startTime.isValid() || !endTime.isValid()) return "";

      const durationInMinutes = endTime.diff(startTime, "minutes");
      if (durationInMinutes < 0) return "";

      const hours = Math.floor(durationInMinutes / 60);
      const minutes = durationInMinutes % 60;

      if (hours > 0 && minutes > 0) {
        return `(${hours}h ${minutes}m)`;
      } else if (hours > 0) {
        return `(${hours} hours)`;
      } else {
        return `(${minutes}m)`;
      }
    } catch (error) {
      return "";
    }
  }

  formatTime(time: any): string {
    if (!time) return "";

    if (this.locationTimezone) {
      try {
        return displayTimeInLocationTimezone(time, this.locationTimezone, "h:mm A");
      } catch (error) {
        console.error("Error formatting time with timezone:", error);
      }
    }

    try {
      const timeMoment = moment(time);
      if (!timeMoment.isValid()) return "Invalid Time";
      return timeMoment.minutes() > 0 ? timeMoment.format("h:mm A") : timeMoment.format("h A");
    } catch {
      return "Invalid Time";
    }
  }

  get location(): string {
    return this.activity?.location?.trim() || "";
  }

  get googleMapsLink(): string {
    if (!this.location) return "";
    const baseMapsUrl = "https://www.google.com/maps/search/?api=1";
    const queryParam = `query=${encodeURIComponent(this.location)}`;
    return `${baseMapsUrl}&${queryParam}`;
  }

  get checkinTimeFormatted(): string {
    if (!this.activity?.checkin_time) return "N/A";
    return this.formatTime(this.activity.checkin_time);
  }

  get checkoutTimeFormatted(): string {
    if (!this.activity?.checkout_time) return "N/A";
    return this.formatTime(this.activity.checkout_time);
  }

  get checkinStatus(): string | null {
    return this.activity?.checkin_status || null;
  }

  get checkoutStatus(): string | null {
    return this.activity?.checkout_status || null;
  }

  get statusText(): string {
    if (!this.activity?.checkin_status) {
      return "No Show";
    }

    if (this.activity?.checkin_status === "OUTSIDE" || this.activity?.checkout_status === "OUTSIDE") {
      return "Outside Geo Fence";
    }

    if (this.activity?.completed) {
      return "Completed";
    }

    return "Completed";
  }

  get statusBadgeClasses(): string {
    const status = this.statusText;

    if (status === "Completed") {
      return "bg-green-100 text-green-700 border-green-200";
    } else if (status === "No Show") {
      return "bg-red-100 text-red-700 border-red-200";
    } else if (status === "Outside Geo Fence") {
      return "bg-orange-100 text-orange-700 border-orange-200";
    }

    return "bg-gray-100 text-gray-700 border-gray-200";
  }

  formatStatusText(status: string): string {
    if (!status) return "";
    return status.replace(/_/g, " ");
  }

  getStatusChipClasses(status: string): string {
    const upperStatus = status?.toUpperCase();

    if (upperStatus === "ON_TIME") {
      return "bg-green-100 text-green-700 border-green-200";
    } else if (upperStatus === "EARLY") {
      return "bg-blue-100 text-blue-700 border-blue-200";
    } else if (upperStatus === "LATE") {
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    } else if (upperStatus === "OUTSIDE") {
      return "bg-red-100 text-red-700 border-red-200";
    }

    return "bg-gray-100 text-gray-700 border-gray-200";
  }

  get assigneeName(): string {
    const user = this.activity?.contractor?.user;
    if (!user) return "";

    const firstName = user.given_name || "";
    const lastName = user.family_name || "";

    return `${firstName} ${lastName}`.trim();
  }

  get assigneeInitials(): string {
    const user = this.activity?.contractor?.user;
    if (!user) return "?";

    const firstName = user.given_name || "";
    const lastName = user.family_name || "";

    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    }

    return "?";
  }

  handleViewNotes(): void {
    this.showNotesModal = true;
  }

  navigateToClient(): void {
    if (!this.project?.id) return;

    this.$router.push({
      name: "project_details-details",
      params: { id: this.project.id },
    });
  }

  navigateToContractor(): void {
    if (!this.activity?.contractor?.id) return;

    this.$router.push({
      name: "contractor_details-about",
      params: { id: this.activity.contractor.id },
    });
  }

  async mounted() {
    await this.loadLocationTimezone();
  }

  async loadLocationTimezone() {
    if (this.activity.timezone) {
      try {
        this.locationTimezone = deserializeTimezone(this.activity.timezone);
        if (this.locationTimezone) {
          return;
        }
      } catch (error) {
        console.error("Error deserializing stored timezone:", error);
      }
    }

    if (this.activity.location && VUE_GOOGLEMAP_API_KEY) {
      try {
        this.locationTimezone = await getTimezoneFromLocationString(
          this.activity.location,
          VUE_GOOGLEMAP_API_KEY
        );
      } catch (error) {
        console.error("Error fetching timezone from location:", error);
        this.locationTimezone = null;
      }
    }
  }
}
